'use client'

import SharedCommunityCard from '@/components/_cards/community/community-card'
import Error from '@/components/common/error'
import ArrowLeft from '@/components/icons/arrow-left'
import ArrowRight from '@/components/icons/arrow-right'
import ShieldIcon from '@/components/icons/shield-icon'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import Link from 'next/link'
import { useEffect, useState } from 'react'

export function getCommunityMembersCount(community) {
  if (!community) return 0
  if (community?._stats?.members?.all !== undefined) {
    return community._stats.members.all
  }

  if (Array.isArray(community.members)) {
    return community.members.length
  }

  return 0
}

export default function PartneredCommunities({ featuredCommunities = [] }) {
  const viewAllCommunitiesHref = '/sim-racing-communities'
  const [carouselApi, setCarouselApi] = useState(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  useEffect(() => {
    if (!carouselApi) return

    const updateScrollState = () => {
      setCanScrollPrev(carouselApi.canScrollPrev())
      setCanScrollNext(carouselApi.canScrollNext())
    }

    updateScrollState()
    carouselApi.on('select', updateScrollState)
    carouselApi.on('reInit', updateScrollState)

    return () => {
      carouselApi.off('select', updateScrollState)
      carouselApi.off('reInit', updateScrollState)
    }
  }, [carouselApi])

  return (
    <div className="bg-linear-to-b mt-9 flex flex-col gap-7 overflow-hidden rounded-t-[60px] from-neutral-blue-800 to-neutral-blue-900 to-50% desktop-xs:gap-10">
      <div className="mx-auto flex flex-col items-center justify-center gap-4 pt-7 font-inter desktop-xs:pt-[60px] desktop-lg:pt-[100px]">
        <div className="flex size-12 items-center justify-center rounded-[5px] bg-white">
          <ShieldIcon className="size-7 stroke-[#017853] stroke-2" />
        </div>
        <h3 className="px-3 text-center text-h-6 font-semibold mobile:px-8 tablet:text-h-5 desktop-xs:text-h-3">
          Become a part of our partnered communities
        </h3>
        <p className="max-w-[550px] px-3 text-center text-p-2 font-normal text-neutral-blue-500 mobile:px-8 desktop-xs:text-p-3">
          Find your new racing family. From casual Trackmania to veteran iRacing
          players, we&apos;ve got you covered.
        </p>
        <Button
          variant="outline"
          asChild
          className="hover:border-shades-0! hover:bg-shades-0! hidden transition-all duration-300 ease-in-out desktop-xs:block"
        >
          <Link
            className="hover:text-black! transition-all ease-in-out"
            href={viewAllCommunitiesHref}
            prefetch={false}
          >
            View all
          </Link>
        </Button>
      </div>

      <div className="desktop-xs:screen-grid overflow-hidden">
        {!featuredCommunities?.length ? (
          <Error
            icon="exclamation"
            size="hero"
            className="mx-auto my-16 w-full max-w-7xl bg-red-600 text-white"
            error={{
              code: 404,
              message: 'Unable to load communities.',
            }}
          />
        ) : (
          <div className="desktop-xs:grid-indent relative min-w-0">
            {canScrollPrev && (
              <button
                type="button"
                onClick={() => carouselApi?.scrollPrev()}
                aria-label="Previous communities"
                className="carousel-btn-bg group absolute -left-20 top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer flex-col justify-center rounded-sm bg-neutral-blue-900 transition-all desktop-xs:flex desktop-lg:w-[109px]"
              >
                <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 group-hover:bg-neutral-blue-600">
                  <ArrowLeft className="m-auto h-4 w-4 stroke-white stroke-2" />
                </div>
              </button>
            )}

            <Carousel
              setApi={setCarouselApi}
              opts={{
                align: 'center',
                slidesToScroll: 'auto',
                containScroll: false,
                breakpoints: {
                  '(min-width: 1024px)': {
                    align: 'start',
                    containScroll: 'trimSnaps',
                  },
                },
              }}
              className="w-full min-w-0"
            >
              <CarouselContent className="pr-6 desktop-xs:pr-0">
                {featuredCommunities.map((community) => (
                  <CarouselItem
                    key={community.id || community.slug}
                    className="basis-[280px] pl-4 last:pr-4 sm:basis-[300px] desktop-xs:basis-1/4 desktop-xs:pl-4 desktop-xs:last:pr-0"
                  >
                    <CommunityCard community={community} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {canScrollNext && (
              <button
                type="button"
                onClick={() => carouselApi?.scrollNext()}
                aria-label="Next communities"
                className="carousel-btn-bg group absolute -right-20 top-1/2 z-10 hidden -translate-y-1/2 cursor-pointer flex-col justify-center rounded-sm bg-neutral-blue-900 transition-all desktop-xs:flex desktop-lg:w-[109px]"
              >
                <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-neutral-blue-600 bg-neutral-blue-700 group-hover:bg-neutral-blue-600">
                  <ArrowRight className="m-auto h-4 w-4 stroke-white stroke-2" />
                </div>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="screen-grid *:grid-indent grid desktop-xs:hidden">
        <Button
          variant="outline"
          asChild
          className="scroll-py-3! px-4! hover:border-shades-0! hover:bg-white! block w-full text-center transition-all duration-300 ease-in-out desktop-xs:hidden"
        >
          <Link
            className="hover:text-black! transition-all ease-in-out"
            href={viewAllCommunitiesHref}
            prefetch={false}
          >
            View all
          </Link>
        </Button>
      </div>
    </div>
  )
}

export function MobileCarousel({ featuredCommunities = [] }) {
  if (!featuredCommunities?.length) {
    return (
      <Error
        icon="exclamation"
        size="hero"
        className="mx-auto my-16 w-full max-w-7xl bg-red-600 text-white"
        error={{ code: 404, message: 'Unable to load communities.' }}
      />
    )
  }

  return (
    <Carousel
      opts={{ align: 'center', slidesToScroll: 'auto', containScroll: false }}
      className="w-full"
    >
      <CarouselContent>
        {featuredCommunities.map((community) => (
          <CarouselItem
            key={community.id || community.slug}
            className="basis-[280px] pl-4 last:pr-4 sm:basis-[300px]"
          >
            <CommunityCard community={community} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  )
}

export function DesktopCarousel({ featuredCommunities = [] }) {
  if (!featuredCommunities?.length) {
    return (
      <Error
        icon="exclamation"
        size="hero"
        className="mx-auto my-16 w-full max-w-7xl bg-red-600 text-white"
        error={{ code: 404, message: 'Unable to load communities.' }}
      />
    )
  }

  return (
    <Carousel opts={{ align: 'start', slidesToScroll: 2 }} className="w-full">
      <CarouselContent>
        {featuredCommunities.map((community) => (
          <CarouselItem
            key={community.id || community.slug}
            className="basis-1/4 first:pl-8"
          >
            <CommunityCard community={community} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  )
}

export function CommunityCard({ community }) {
  return (
    <SharedCommunityCard
      community={{
        ...community,
        _stats: { members: { all: getCommunityMembersCount(community) } },
      }}
      prefetch={false}
      imageSizes="(min-width: 1280px) 300px, (min-width: 1024px) calc((100vw - 96px) / 4), (min-width: 640px) 300px, 280px"
    />
  )
}
